import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../core/breadcrumbs";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Link } from "react-router-dom";
import { Filter, Sliders, Box, Zap } from "react-feather";
import Select from "react-select";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

const InventoryReport = () => {
  const { showToast } = useToast();
  const [inventoryData, setInventoryData] = useState([]);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prev) => !prev);
  };

  // Options for dropdowns
  const sortOptions = [
    { value: "sortByDate", label: "Sort by Date" },
    { value: "140923", label: "14 09 23" },
    { value: "110923", label: "11 09 23" },
  ];

  const productOptions = [
    { value: "chooseProduct", label: "Choose Product" },
    ...new Set(
      inventoryData.map((item) => ({
        value: item.name || "Unknown",
        label: item.name || "Unknown",
      }))
    ),
  ];

  const categoryOptions = [
    { value: "chooseCategory", label: "Choose Category" },
    ...new Set(
      inventoryData.map((item) => ({
        value: item.category_name || "Unknown",
        label: item.category_name || "Unknown",
      }))
    ),
  ];

  // Fetch inventory report
  const fetchInventoryReport = async () => {
    try {
      const response = await apiClient.post(`/generateInventoryReport`, {
        type: "preview",
      });
      if (response?.success && Array.isArray(response.data)) {
        setInventoryData(response.data);
        if (response.data.length === 0) {
          showToast("No inventory data found", "warning");
        }
      } else {
        setInventoryData([]);
        showToast("No inventory data found", "warning");
      }
    } catch (error) {
      console.error("Failed to fetch inventory report:", error);
      showToast("Failed to fetch inventory report", "error");
    }
  };

  useEffect(() => {
    fetchInventoryReport();
  }, []);

  // Handle Excel export
  const handleExportExcel = async () => {
    try {
      const response = await apiClient.post(`/generateInventoryReport`, { });
      const fileUrl = response?.path;

      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "inventory-report.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Inventory report exported to Excel successfully", "success");
      } else {
        showToast("No file URL found in response", "error");
      }
    } catch (error) {
      console.error("Failed to export inventory report to Excel:", error);
      showToast("Failed to export inventory report to Excel", "error");
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.text("Inventory Report", 14, 20);
      doc.setFontSize(12);
      doc.text("Generated on: July 30, 2025, 12:04 PM PKT", 14, 30);

      // Table
      doc.autoTable({
        startY: 40,
        head: [["Product Name", "SKU", "Category", "Unit", "In Stock Qty"]],
        body: inventoryData.flatMap((product) => {
          const rows = [];
          const hasVariants = product.variants && product.variants.length > 0;

          // Main product row
          rows.push([
            product.name || "N/A",
            product.sku || "N/A",
            product.category_name || "N/A",
            product.unit || "N/A",
            product.product_type === "single"
              ? product.quantity || "0"
              : hasVariants
                ? product.variants.reduce(
                  (sum, variant) => sum + (variant.quantity || 0),
                  0
                )
                : "N/A",
          ]);

          // Variant rows
          if (hasVariants) {
            product.variants.forEach((variant) => {
              rows.push([
                `  -> ${variant.name} (${variant.variant_value})` || "N/A",
                variant.sku || "N/A",
                product.category_name || "N/A",
                product.unit || "N/A",
                variant.quantity || "0",
              ]);
            });
          }

          return rows;
        }),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [100, 100, 100], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 40 },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: "right" });
      }

      // Save PDF
      doc.save("inventory-report.pdf");
      showToast("Inventory report exported to PDF successfully", "success");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      showToast("Failed to export inventory report to PDF", "error");
    }
  };

  // Render product rows including variants
  const renderProductRows = () => {
    return inventoryData.flatMap((product) => {
      const rows = [];
      const hasVariants = product.variants && product.variants.length > 0;

      // Main product row
      rows.push(
        <tr key={product.id}>
          <td>
            <label className="checkboxs">
              <input type="checkbox" />
              <span className="checkmarks" />
            </label>
          </td>
          <td className="productimgname">
            <div className="view-product me-2">
              <Link to="#">
                {product.image && product.image.length > 0 ? (
                  <ImageWithBasePath src={product.image[0]} alt="product" />
                ) : (
                  <ImageWithBasePath
                    src="assets/img/products/default.png"
                    alt="product"
                  />
                )}
              </Link>
            </div>
            <Link to="#">{product.name || "N/A"}</Link>
          </td>
          <td>{product.sku || "N/A"}</td>
          <td>{product.category_name || "N/A"}</td>
          <td>{product.unit || "N/A"}</td>
          <td>
            {product.product_type === "single"
              ? product.quantity || "0"
              : hasVariants
                ? product.variants.reduce(
                  (sum, variant) => sum + (variant.quantity || 0),
                  0
                )
                : "N/A"}
          </td>
        </tr>
      );

      // Variant rows
      if (hasVariants) {
        product.variants.forEach((variant) => {
          rows.push(
            <tr key={`${product.id}-variant-${variant.id}`} className="variant-row">
              <td></td>
              <td className="productimgname">
                <div className="view-product me-2"></div>
                <Link to="#">↳ {variant.name} ({variant.variant_value})</Link>
              </td>
              <td>{variant.sku || "N/A"}</td>
              <td>{product.category_name || "N/A"}</td>
              <td>{product.unit || "N/A"}</td>
              <td>{variant.quantity || "0"}</td>
            </tr>
          );
        });
      }

      return rows;
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Inventory Report"
          subtitle="Manage Your Inventory Report"
          onExcelClick={handleExportExcel}
          onPDFClick={handleExportPDF}
        />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm formsearch"
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
              <div className="search-path">
                <Link
                  className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                  id="filter_search"
                  onClick={toggleFilterVisibility}
                >
                  <Filter className="filter-icon" />
                  <span>
                    <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                  </span>
                </Link>
              </div>
              <div className="form-sort stylewidth">
                <Sliders className="info-img" />
                <Select
                  className="select"
                  options={sortOptions}
                  placeholder="Sort by Date"
                />
              </div>
            </div>
            <div
              className={`card ${isFilterVisible ? "visible" : ""}`}
              id="filter_inputs"
              style={{ display: isFilterVisible ? "block" : "none" }}
            >
              <div className="card-body pb-0">
                <div className="row">
                  <div className="col-lg-3">
                    <div className="input-blocks">
                      <Box className="info-img" />
                      <Select className="select" options={productOptions} />
                    </div>
                  </div>
                  <div className="col-lg-3">
                    <div className="input-blocks">
                      <Zap className="info-img" />
                      <Select className="select" options={categoryOptions} />
                    </div>
                  </div>
                  <div className="col-lg-6 col-sm-6 col-12">
                    <div className="input-blocks">
                      <Link to="#" className="btn btn-filters ms-auto">
                        <i data-feather="search" className="feather-search" /> Search
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table datanew">
                <thead>
                  <tr>
                    <th className="no-sort">
                      <label className="checkboxs">
                        <input type="checkbox" id="select-all" />
                        <span className="checkmarks" />
                      </label>
                    </th>
                    <th>Product Name</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th>In Stock Qty</th>
                  </tr>
                </thead>
                <tbody>{renderProductRows()}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryReport;